// src/Pages/Backoffice.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionUser } from '../SessionContext';
import { resolveSession } from '../lib/session';
import { API_BASE } from '../config/api.js';
import BackofficeTopbar from '../components/BackOfficeComp/BackofficeTopbar';
import BackofficeModuleTabs from '../components/BackOfficeComp/BackofficeModuleTabs';
import BackofficeQueriesPanel from '../components/BackOfficeComp/BackofficeQueriesPanel';
import BackofficeDashboardPanel from '../components/BackOfficeComp/BackofficeDashboardPanel';
import BackofficePerformancePanel from '../components/BackOfficeComp/BackofficePerformancePanel';
import BackofficeApprovalPanel from '../components/BackOfficeComp/BackofficeApprovalPanel';
import BackofficeRecordingsPanel from '../components/BackOfficeComp/BackofficeRecordingsPanel';
import './Backoffice.css';

/* -------------------- Lists -------------------- */
const MODULE_TABS = [
  { key: 'queries', label: 'Queries' },
  { key: 'approval', label: "Advisor's Approval" },
  { key: 'performance', label: 'Advocate Performance' },
  { key: 'dashboard', label: "Approval's Dashboard" },
  { key: 'recordings', label: 'Call Recordings' },
  {
    key: 'reports',
    label: 'Reports',
    children: [
      { key: 'cdnReport', label: 'CDN Route Point', to: '/reports' },
      { key: 'dnisReport', label: 'DNIS Report', to: '/reports/dnis-statistics' },
      { key: 'dailyCallReport', label: 'Daily Call Report', to: '/reports/daily-call-report' },
      { key: 'agentStateReport', label: 'Agent State Report', to: '/reports/agent-state-report' },
      { key: 'agentLoginLogoutReport', label: 'Agent Login / Logout', to: '/reports/agent-login-logout-report' },
      { key: 'agentPerformanceReport', label: 'Agent Performance', to: '/reports/agent-performance-report' },
      { key: 'agentCallHistoryReport', label: 'Agent Call History', to: '/reports/agent-call-history' },
      { key: 'originatorDispositionReport', label: 'Originator By Disposition', to: '/reports/originator-by-disposition' },
    ],
  },
  {
    key: 'historicalReports',
    label: 'Historical Reports',
    children: [
      { key: 'abandonedReport', label: 'Abandoned Report', to: '/reports/historical/abandoned' },
      { key: 'callbackVoicemailReport', label: 'Callback / Voicemail', to: '/reports/historical/callback-voicemail' },
    ],
  },
];

const getBackofficeRoleCodes = (primaryRole) => {
  const roleCodes = new Set();
  const addRole = (value) => {
    if (Array.isArray(value)) {
      value.forEach(addRole);
      return;
    }
    if (value && typeof value === 'object') {
      [value.RoleName, value.roleName, value.ShortCode, value.shortCode].forEach(addRole);
      return;
    }
    const normalized = String(value || '').trim().toUpperCase();
    if (normalized) roleCodes.add(normalized);
  };

  addRole(primaryRole);
  try {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    addRole(storedUser.RoleName || storedUser.roleName);
    addRole(storedUser.Roles || storedUser.roles);
  } catch {
    // Ignore malformed persisted session data.
  }
  return roleCodes;
};

const statusToClass = (status = '') => {
  const normalized = String(status).toLowerCase();
  if (normalized.includes('accept')) return 'accepted';
  if (normalized.includes('reject')) return 'rejected';
  if (normalized.includes('return')) return 'returned';
  return 'pending';
};

const formatLiveDate = () => new Intl.DateTimeFormat('en-US', {
  month: 'numeric',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
  hour12: true,
}).format(new Date());

const Backoffice = () => {
  const navigate = useNavigate();
  const { user: sessionUser } = useSessionUser();
  const { userId, username, roleName } = useMemo(() => resolveSession(sessionUser), [sessionUser]);
  const isViewOnly = roleName === 'BOV';
  const roleCodes = useMemo(() => getBackofficeRoleCodes(roleName), [roleName]);
  const canViewRecordings = Array.from(roleCodes).some((role) =>
    ['AD', 'BO', 'BOV', 'RCV', 'RCD'].includes(role)
  );
  const visibleModuleTabs = useMemo(
    () => MODULE_TABS.filter((tab) => tab.key !== 'recordings' || canViewRecordings),
    [canViewRecordings]
  );

  const [activeModule, setActiveModule] = useState('queries');
  const [reviewScreen, setReviewScreen] = useState('advisor');
  const [theme, setTheme] = useState('light');
  const [liveDate, setLiveDate] = useState('');

  // Agents (names) + categories
  const [agentOptions, setAgentOptions] = useState([]);
  const [selectedAgentName, setSelectedAgentName] = useState([]);
  const [agentsErr, setAgentsErr] = useState('');

  const [mainCats, setMainCats] = useState([]);       // array of strings
  const [mainCatsErr, setMainCatsErr] = useState('');

  // Filters
  const [form, setForm] = useState({
    query: "",          // BASE_STATUSES ('' means placeholder)
    status: "",         // Open / Accepted ('' means placeholder)
    queryId: "",
    mainCat: "",        // '' means placeholder
    date: "",
    dateTo: "",
  });

  // Results
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [activeQid, setActiveQid] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 20;
  const lastPayloadRef = React.useRef(null);
  const [selectedApprovalId, setSelectedApprovalId] = useState('');
  const [approvalFilterActive, setApprovalFilterActive] = useState(false);

  const getRowKey = (row) => String(row?.queryId ?? row?.QID ?? row?.QueryID ?? '');

  const handleLogout = () => {
    navigate('/logout');
  };

  useEffect(() => {
    const saved = localStorage.getItem('slacc-theme');
    if (saved) {
      setTheme(saved);
      return;
    }
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setTheme('dark');
    }
  }, []);

  useEffect(() => {
    document.body.setAttribute('data-theme', theme);
    localStorage.setItem('slacc-theme', theme);
  }, [theme]);

  useEffect(() => {
    document.body.setAttribute('data-app', 'backoffice');
    return () => {
      document.body.removeAttribute('data-app');
    };
  }, []);

  useEffect(() => {
    const tick = () => setLiveDate(formatLiveDate());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    // When approval panel's own filter is active, it manages its own rows —
    // don't override the selection based on the queries-tab rows.
    if (approvalFilterActive) return;

    const rowKeys = rows
      .map((row) => getRowKey(row))
      .map((value) => String(value || '').trim())
      .filter(Boolean);

    if (rowKeys.length === 0) {
      setActiveQid('');
      setSelectedApprovalId('');
      return;
    }

    if (!selectedApprovalId || !rowKeys.includes(String(selectedApprovalId).trim())) {
      setSelectedApprovalId(rowKeys[0]);
    }

    if (activeQid && !rowKeys.includes(String(activeQid).trim())) {
      setActiveQid('');
    }
  }, [activeQid, approvalFilterActive, rows, selectedApprovalId]);

  function handleRowDoubleClick(row) {
    const qid = getRowKey(row);
    if (!qid) return;
    sessionStorage.setItem('bo.activeRow', JSON.stringify(row));
    setActiveQid(qid);
    setSelectedApprovalId(qid);
    setActiveModule('approval');
  }
  const updateForm = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  /* -------- Load categories -------- */
  useEffect(() => {
    (async () => {
      try {
        setMainCatsErr('');
        const res = await fetch(`${API_BASE}/complaint-categories`, { headers: { Accept: 'application/json' } });
        const json = await res.json();
        const list = Array.isArray(json?.data) ? json.data : (Array.isArray(json) ? json : []);
        setMainCats(list);
      } catch (e) {
        setMainCatsErr(e.message || 'Failed to load categories');
        setMainCats([]);
      }
    })();
  }, []);

  /* -------- Load agents for department (keeps id & name; we submit NAMES) -------- */
  async function loadAgentsForDepartment(departmentName, setOptions, setError) {
    setError?.('');
    if (!departmentName || !departmentName.trim()) {
      setError?.('Please select a department first.');
      setOptions?.([]);
      return;
    }
    try {
      const params = new URLSearchParams();
      params.set('departmentName', departmentName);
      params.set('departmantname', departmentName);
      params.set('departmentname', departmentName);
      const res = await fetch(
        `${API_BASE}/BoFillagents?${params.toString()}`,
        { headers: { Accept: 'application/json' } }
      );
      const text = await res.text();
      let json = null;
      try { json = text ? JSON.parse(text) : null; } catch {}
      const list =
        Array.isArray(json?.data) ? json.data :
        Array.isArray(json?.agents) ? json.agents :
        Array.isArray(json) ? json : [];
      const options = list.map((r, idx) => {
        const id = r?.id ?? r?.ID ?? r?.UserID ?? r?.userId ?? r?.userid ?? (idx + 1);
        const name =
          r?.FullName ?? r?.fullName ??
          r?.LoginName ?? r?.loginName ??
          r?.UserName ?? r?.username ??
          r?.AgentName ?? r?.agentName ??
          r?.name ??
          '';
        return { id, name: name || `Agent ${idx + 1}` };
      });
      setOptions?.(options);
    } catch (e) {
      setError?.(e.message || 'Failed to load agents');
      setOptions?.([]);
    }
  }
  useEffect(() => {
    const deptName = JSON.parse(localStorage.getItem('user') || '{}')?.DepartmentName || '';
    loadAgentsForDepartment(deptName, setAgentOptions, setAgentsErr);
  }, []);

  /* -------- Close GI -------- */
  async function handleCloseGI() {
    if (isViewOnly) return;
    try {
      const editedBy =
        Number(userId) ||
        Number((JSON.parse(localStorage.getItem('user') || '{}').UserID) || 0);
      if (!editedBy) { alert('UserID missing. Please re-login.'); return; }

      const payload = { editedBy, routedToUser: editedBy, dryRun: false };

      const res = await fetch(`${API_BASE}/complaints/close-gi`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept':'application/json' },
        body: JSON.stringify(payload), credentials: 'include',
      });

      const text = await res.text(); let json = null; try { json = text ? JSON.parse(text) : null; } catch {}
      if (!res.ok || json?.success === false) throw new Error(json?.message || text || 'Request failed');

      alert(`GI Closed.\nFound: ${json?.found ?? json?.data?.found ?? 0}\nCopied: ${json?.copied ?? json?.data?.copied ?? 0}\nUpdated: ${json?.updated ?? json?.data?.updated ?? 0}`);
    } catch (e) {
      alert('Close GI failed: ' + (e?.message || e));
    }
  }

  /* -------- SHOW: validate + fetch -------- */
  function isFuture(yyyymmdd) {
    if (!yyyymmdd) return false;
    const now = new Date();
    const today = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0'),
    ].join('-');
    return yyyymmdd > today;
  }

  async function fetchPage(payload, page) {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/history-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...payload, page, perPage: PAGE_SIZE }),
      });
      const text = await res.text(); let json = null; try { json = text ? JSON.parse(text) : null; } catch {}
      if (!res.ok) throw new Error(json?.message || text || 'Request failed');

      const list = Array.isArray(json?.data) ? json.data : [];
      const total = json?.total ?? json?.totalCount ?? json?.Total ?? json?.TotalCount ?? list.length;
      const mapped = list.map(r => ({
        queryId:  r.QID ?? r.QueryID ?? r.qid ?? '',
        callerId: r.CallersID ?? r.CallerID ?? r.callersid ?? r.callerid ?? '',
        contactPerson: r.ContactPerson ?? r.Name ?? r.contactperson ?? r.contactPerson ?? '',
        status:   r.Status ?? r.status ?? '',
        priority: r.Priority ?? r.priority ?? r.PriorityName ?? r.priorityName ?? '',
        contactNumber: r.ContactNumber ?? r.PhoneNo ?? r.MobileNo ?? r.contactnumber ?? '',
        routedToDepartment: r.RoutedToDepartment ?? r.routedtodepartment ?? '',
        edited: r.Edited ?? r.edited ?? '',
        mainCat: r.MainCat ?? r.Category ?? r.maincat ?? r.category ?? '',
        cat: r.Cat ?? r.SubCategory ?? r.cat ?? '',
        subCat: r['Sub-Cat'] ?? r.SubCatChild ?? r['sub-cat'] ?? r.subcat ?? '',
        office: r.Office ?? r.office ?? '',
        createdOn: r.CreatedOn ?? r.createdon ?? '',
        modifiedOn: r.ModifiedOn ?? r.modifiedon ?? '',
        closedOn: r.ClosedOn ?? r.closedon ?? '',
        history: r.History ?? r.history ?? '',
        notes: r.Notes ?? r.notes ?? '',
        tags: r.TAGS ?? r.Tags ?? r.tags ?? '',
        createdBy: r.CreatedBy ?? r.createdby ?? '',
        modifiedBy: r.ModifyiedBy ?? r.ModifiedBy ?? r.modifiedby ?? '',
        routedOn: r.RoutedOn ?? r.routedon ?? '',
        routedBy: r.RoutedBy ?? r.routeby ?? r.routedby ?? '',
        __raw: r,
      }));
      setRows(mapped);
      setTotalCount(total);
      setCurrentPage(page);
      setActiveQid('');
    } catch (e) {
      setErr(e.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }

  async function handleShow() {
    setErr('');

    const loginUser = JSON.parse(localStorage.getItem('user') || '{}');
    const departmentName = loginUser?.DepartmentName || '';
    if (!departmentName) { setErr('Department not found in login payload. Please re-login.'); return; }
    if (form.date && isFuture(form.date))    { setErr("Date can't be greater than today (From)."); return; }
    if (form.dateTo && isFuture(form.dateTo)){ setErr("Date can't be greater than today (To).");  return; }
    if (form.date && form.dateTo) {
      if (new Date(form.date) > new Date(form.dateTo)) { setErr('Date From cannot be after Date To.'); return; }
    }

    const payload = { departmentName };
    if (form.query) {
      payload.acceptStatus = form.query;
      payload.userName = loginUser?.UserName || loginUser?.username || loginUser?.LoginName || username || '';
    }
    if (form.status && form.status !== 'ALL') payload.status = form.status;
    if (form.queryId?.trim()) payload.queryId = form.queryId.trim();
    if (form.mainCat)    payload.mainCat  = form.mainCat;
    if (form.date)       payload.dateFrom = form.date;
    if (form.dateTo)     payload.dateTo   = form.dateTo;
    if (Array.isArray(selectedAgentName) && selectedAgentName.length) {
      payload.agentNames = selectedAgentName;
    }
    lastPayloadRef.current = payload;
    await fetchPage(payload, 1);
  }

  const handlePageChange = async (newPage) => {
    if (!lastPayloadRef.current || loading) return;
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    if (newPage < 1 || newPage > totalPages) return;
    await fetchPage(lastPayloadRef.current, newPage);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const resultsLabel = totalCount > 0
    ? `${totalCount} results (page ${currentPage} of ${totalPages})`
    : rows.length === 1 ? '1 result' : `${rows.length} results`;
  const pendingCount = rows.filter((r) => String(r.status || '').toLowerCase().includes('pending')).length;
  const selectedApproval = rows.find((r) => getRowKey(r) === selectedApprovalId) || null;

  return (
    <div className="app-shell">
      <BackofficeTopbar
        username={username}
        roleName={roleName}
        liveDate={liveDate}
        theme={theme}
        onToggleTheme={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        onLogout={handleLogout}
      />

      <BackofficeModuleTabs
        tabs={visibleModuleTabs}
        activeModule={activeModule}
        onChange={setActiveModule}
      />

      <main className="module-stage">
        <BackofficeQueriesPanel
          isActive={activeModule === 'queries'}
          form={form}
          onFormUpdate={updateForm}
          selectedAgentName={selectedAgentName}
          onSelectAgent={setSelectedAgentName}
          agentOptions={agentOptions}
          agentsErr={agentsErr}
          mainCats={mainCats}
          mainCatsErr={mainCatsErr}
          isViewOnly={isViewOnly}
          onCloseGI={handleCloseGI}
          onShow={handleShow}
          loading={loading}
          err={err}
          rows={rows}
          resultsLabel={resultsLabel}
          activeQid={activeQid}
          onRowDoubleClick={handleRowDoubleClick}
          getRowKey={getRowKey}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />

        <BackofficeDashboardPanel isActive={activeModule === 'dashboard'} />

        <BackofficePerformancePanel isActive={activeModule === 'performance'} />

        {canViewRecordings && (
          <BackofficeRecordingsPanel isActive={activeModule === 'recordings'} />
        )}

        <BackofficeApprovalPanel
          isActive={activeModule === 'approval'}
          rows={rows}
          pendingCount={pendingCount}
          selectedApprovalId={selectedApprovalId}
          onSelectApproval={setSelectedApprovalId}
          selectedApproval={selectedApproval}
          currentUserId={userId}
          currentUser={username}
          currentRole={roleName}
          isViewOnly={isViewOnly}
          reviewScreen={reviewScreen}
          onReviewScreenChange={setReviewScreen}
          getRowKey={getRowKey}
          statusToClass={statusToClass}
          onCloseGI={handleCloseGI}
          onRefreshRows={handleShow}
          onApprovalFilterActiveChange={setApprovalFilterActive}
          queryCurrentPage={currentPage}
          queryTotalPages={totalPages}
          queryTotalCount={totalCount}
          queryLoading={loading}
          onQueryPageChange={handlePageChange}
        />
      </main>
    </div>
  );

};

export default Backoffice;
